import {
  Filter,
  GetGroupResponse,
  GetOrganizationResponse,
  NormalizedPolicies,
  Override,
  PoliciesByCategory,
  Policy,
  TransformedResponse,
} from "../types";

/**
 * normalizePolicies
 * @param response Array of policies
 * @returns Normalized object keyed on policy.id
 */
export const normalizePolicies = (
  response: GetOrganizationResponse | GetGroupResponse,
): TransformedResponse => ({
  policies: response.policies.reduce(
    (acc: NormalizedPolicies, policy: Policy) => {
      acc[policy.id] = policy;
      return acc;
    },
    {},
  ),
  estimate: response.reprocess_time_estimate,
  rl_scan_level: response.rl_scan_level,
  ...(response.auto_approval_enabled !== undefined
    ? { auto_approval_enabled: response.auto_approval_enabled }
    : {}),
});

/**
 *
 * @param policies Normalized object keyed on policy.id
 * @returns Normalized object keyed on policy.category with each value being a normalized object of policies keyed on policy.id
 */
export const groupPoliciesByCategory = (
  policies: Policy[],
): PoliciesByCategory => {
  return policies.reduce((acc: PoliciesByCategory, policy: Policy) => {
    if (acc[policy.category]) acc[policy.category].push(policy);
    else acc[policy.category] = [policy];
    return acc;
  }, {});
};

type FilterPolicies = {
  filter: Filter;
  policies: Policy[];
  overrides: Override[];
  reverts: string[];
  scanLevel: number | null;
};

export const filterPolicies = ({
  filter,
  policies,
  overrides,
  reverts,
  scanLevel,
}: FilterPolicies): Policy[] => {
  return policies.filter((policy) => {
    if (filter.policyId && policy.id !== filter.policyId) {
      return false;
    }

    if (
      filter.showEdited &&
      !(policy.override || isPolicyInOverrides({ policy, overrides }))
    ) {
      return false;
    }

    if (
      filter.showCustomized &&
      !isPolicyCustomizingLevel({
        policy,
        overrides,
        reverts,
        scanLevel,
      })
    ) {
      return false;
    }

    return true;
  });
};

type IsPolicyInOverrides = {
  overrides: Override[];
  policy: Policy;
};

export const isPolicyInOverrides = ({
  overrides,
  policy,
}: IsPolicyInOverrides): boolean => {
  return overrides.some((override) => {
    return override.id === policy.id;
  });
};

type IsPolicyCustomizingLevel = {
  policy: Policy;
  overrides: Override[];
  reverts: string[];
  scanLevel: number | null;
};

export const isPolicyCustomizingLevel = ({
  policy,
  overrides,
  reverts,
  scanLevel,
}: IsPolicyCustomizingLevel): boolean => {
  const policyOverride = overrides.find(
    (override) => override.id === policy.id,
  );

  const policyShouldHaveBlocker =
    policy.rl_level > 0 && scanLevel !== null && policy.rl_level <= scanLevel;

  if (policyShouldHaveBlocker) {
    const currentOverrideNotBlocking =
      policyOverride !== undefined &&
      (!policyOverride.blocker || !policyOverride.enabled);
    const cachedOverrideNotBlocking =
      policy.override !== undefined &&
      (!policy.override.blocker || !policy.override.enabled);
    const inheritedOverrideNotBlocking =
      policy.inherited.is_user_override &&
      (!policy.inherited.blocker || !policy.inherited.enabled);
    const hasOverride =
      policy.override !== undefined || policyOverride !== undefined;

    if (currentOverrideNotBlocking) {
      // If policy has unsaved override which is not blocking level is customized
      return true;
    } else if (cachedOverrideNotBlocking && !reverts.includes(policy.id)) {
      // If policy has saved override which is not blocking, and which was not reverted, level is customized
      return true;
    } else if (inheritedOverrideNotBlocking && !hasOverride) {
      // If policy only has inherited override which is not blocking level is customized
      return true;
    }
  }

  return false;
};
